import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  fetchRenterTaskByTaskId,
  postAppointmentOutcome,
  postResidentAvailability,
} from 'store/redux/maintenance/actions';
import { showToast } from 'store/redux/toast';
import type { FormValues } from './appointment-outcome-modal.types';

const TOAST_AUTO_DELETE_MS = 4000;
const SUCCESS_RESOLVED =
  "Thanks! We'll let the homeowner know everything went well.";
const SUCCESS_NOT_RESOLVED_RESCHEDULE =
  "We're reviewing the case and will follow up shortly.";
const SUCCESS_NOT_RESOLVED_CANCEL =
  "We've notified the Pro and updated your request accordingly.";
const SUCCESS_NEVER_ARRIVED_RESCHEDULE =
  "We're reviewing the case and will follow up shortly.";
const SUCCESS_NEVER_ARRIVED_CANCEL =
  'Our team will review the missed appointment and follow up if needed.';

function getSuccessMessage(
  isNeverArrived: boolean,
  resolved: boolean | '',
  noLongerNeedsVisit: boolean,
): string {
  if (!isNeverArrived && resolved === true) return SUCCESS_RESOLVED;
  if (isNeverArrived) {
    return noLongerNeedsVisit
      ? SUCCESS_NEVER_ARRIVED_CANCEL
      : SUCCESS_NEVER_ARRIVED_RESCHEDULE;
  }
  return noLongerNeedsVisit
    ? SUCCESS_NOT_RESOLVED_CANCEL
    : SUCCESS_NOT_RESOLVED_RESCHEDULE;
}

const GENERIC_ERROR_MESSAGE =
  'Something went wrong submitting your response. Please try again.';

function extractErrorMessage(err: unknown): string {
  if (Array.isArray(err) && err.length > 0 && err[0]?.message) {
    return err[0].message;
  }
  if (err && typeof err === 'object' && 'message' in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }
  return GENERIC_ERROR_MESSAGE;
}

type UseAppointmentOutcomeSubmitArgs = {
  maintenanceUniqueId: string;
  appointmentUniqueId: string;
  onDone: () => void;
};

export function useAppointmentOutcomeSubmit({
  maintenanceUniqueId,
  appointmentUniqueId,
  onDone,
}: UseAppointmentOutcomeSubmitArgs) {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: FormValues) => {
    try {
      setIsSubmitting(true);

      const isResolvableBranch =
        values.arrivalStatus === 'onTime' ||
        values.arrivalStatus === 'arrivedLate';
      const isNeverArrived = values.arrivalStatus === 'neverArrived';
      const isInReschedulePath =
        (isResolvableBranch && values.resolved === false) || isNeverArrived;

      const basePayload = {
        appointmentUniqueId,
        arrivalStatus: values.arrivalStatus,
        outcome:
          values.resolved === true
            ? ('Resolved' as const)
            : ('NotResolved' as const),
      };

      let outcomePayload: Record<string, unknown> = basePayload;

      if (isResolvableBranch && values.resolved === true) {
        outcomePayload = {
          ...basePayload,
          workRating: values.workRating,
          workComments: values.workComments || undefined,
        };
      } else if (isInReschedulePath) {
        outcomePayload = {
          ...basePayload,
          wantsToReschedule: !values.noLongerNeedsVisit,
          reason: values.noLongerNeedsVisit
            ? 'Resident no longer needs this visit'
            : isNeverArrived
              ? 'Pro Never Arrived'
              : undefined,
        };
      }

      await dispatch(
        postAppointmentOutcome(maintenanceUniqueId, outcomePayload),
      );

      if (isInReschedulePath && !values.noLongerNeedsVisit) {
        const residentAvailability = values.requestorAvailability.filter(
          (d) => d.times.length > 0,
        );

        if (residentAvailability.length > 0) {
          await dispatch(
            postResidentAvailability(maintenanceUniqueId, {
              appointmentUniqueId,
              residentAvailability,
            }),
          );
        }
      }

      await dispatch(fetchRenterTaskByTaskId(maintenanceUniqueId));

      dispatch(
        showToast(
          {
            message: getSuccessMessage(
              isNeverArrived,
              values.resolved,
              values.noLongerNeedsVisit,
            ),
            created: true,
          },
          { autoDelete: true, autoDeleteTime: TOAST_AUTO_DELETE_MS },
        ),
      );

      onDone();
    } catch (err) {
      dispatch(
        showToast(
          { message: extractErrorMessage(err), error: true },
          { autoDelete: true, autoDeleteTime: TOAST_AUTO_DELETE_MS },
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return { isSubmitting, handleSubmit };
}
